import type { BrowsableDocument, DocumentGroupingMode, DocumentSortMode } from './useDocumentBrowserStore';
import { DocumentStatusBadge } from './DocumentStatusBadge';
import {
  formatDateShort,
  formatDocumentTypeLabel,
  formatFileSize,
  formatLanguageLabel,
  formatVersionLabel,
  getDocumentDisplayTitle,
  getSourceKindLabel,
  getUploaderLabel,
} from './documentBrowserUtils';
import { getDocumentGroups, getSortedDocuments } from './documentBrowserCollections';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';

interface DocumentCardGridProps {
  documents: BrowsableDocument[];
  loading: boolean;
  groupBy: DocumentGroupingMode;
  sortBy: DocumentSortMode;
  onSelectDocument: (document: BrowsableDocument) => void;
}

export function DocumentCardGrid({ documents, loading, groupBy, sortBy, onSelectDocument }: DocumentCardGridProps) {
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-44 rounded-2xl" />
        ))}
      </div>
    );
  }

  if (documents.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Keine Dokumente in diesem Workspace.
      </div>
    );
  }

  const sortedDocuments = getSortedDocuments(documents, sortBy);
  const groups = groupBy === 'none' ? [] : getDocumentGroups(documents, groupBy, sortBy);

  if (groupBy === 'none') {
    return <DocumentCardList documents={sortedDocuments} onSelectDocument={onSelectDocument} />;
  }

  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <section key={group.key} className="space-y-3">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-foreground">{group.label}</h3>
            <Badge variant="secondary" className="rounded-full px-2.5 py-0 text-[11px]">
              {group.documents.length}
            </Badge>
          </div>
          <DocumentCardList documents={group.documents} onSelectDocument={onSelectDocument} />
        </section>
      ))}
    </div>
  );
}

function DocumentCardList({
  documents,
  onSelectDocument,
}: {
  documents: BrowsableDocument[];
  onSelectDocument: (document: BrowsableDocument) => void;
}) {
  return (
    <div aria-label="Dokumentkarten" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
      {documents.map((document) => (
        <Card key={document.id} className="overflow-hidden border-border/80 bg-card shadow-sm">
          <CardContent className="flex h-full flex-col gap-4 p-5">
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <DocumentStatusBadge status={document.status} />
                <Badge variant="outline">{getSourceKindLabel(document.sourceKind)}</Badge>
              </div>
              <div>
                <p className="line-clamp-2 text-base font-semibold text-foreground">{getDocumentDisplayTitle(document)}</p>
                {document.displayTitle && (
                  <p className="mt-1 line-clamp-1 text-xs text-muted-foreground">{document.fileName}</p>
                )}
                <p className="mt-1 text-sm text-muted-foreground">
                  {document.sourceLabel ?? 'Manueller Upload'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm text-muted-foreground">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em]">Hochgeladen</p>
                <p className="mt-1 text-foreground">{formatDateShort(document.createdAt)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em]">Version</p>
                <p className="mt-1 text-foreground">{formatVersionLabel(document)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em]">Dateigroesse</p>
                <p className="mt-1 text-foreground">{formatFileSize(document.fileSizeBytes)}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em]">Uploader</p>
                <p className="mt-1 text-foreground">{getUploaderLabel(document)}</p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {document.documentType && <Badge variant="secondary">{formatDocumentTypeLabel(document.documentType)}</Badge>}
              {document.language && <Badge variant="secondary">{formatLanguageLabel(document.language)}</Badge>}
              {document.machineLabel && <Badge variant="secondary">{document.machineLabel}</Badge>}
              {document.machineCategory && <Badge variant="secondary">{document.machineCategory}</Badge>}
              {document.machineManufacturer && <Badge variant="secondary">{document.machineManufacturer}</Badge>}
            </div>

            <div className="mt-auto flex justify-end">
              <Button variant="secondary" size="sm" onClick={() => onSelectDocument(document)}>
                Details
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}